import { io, Socket } from "socket.io-client";
import { getSocketOrigin } from "./apiBridge";
import { getStoredToken } from "./authService";

function getDeviceFingerprint(): string {
  const key = 'xf_device_fingerprint';
  let fp: string | null = null;
  try { fp = localStorage.getItem(key); } catch (e) { console.warn('[socketService] 读取设备指纹失败:', e); }
  if (!fp) {
    fp = `${navigator.platform || 'unknown'}_${Date.now().toString(36)}_${crypto.randomUUID()}`;
    try { localStorage.setItem(key, fp); } catch (e) { console.warn('[socketService] 保存设备指纹失败:', e); }
  }
  return fp;
}

const DEVICE_FINGERPRINT = getDeviceFingerprint();

const HEARTBEAT_KEY = 'xf_page_heartbeat';
const SESSION_SKIP_BOOT = 'xf_skip_boot';
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let disconnectSince: number | null = null;
const DISCONNECT_FALLBACK_RELOAD_MS = 10 * 60_000; // 10 min extreme fallback (renderer truly stuck)
const HEARTBEAT_INTERVAL_MS = 5_000;
const HEALTHCHECK_INTERVAL_MS = 30_000;

export type SocketStatus = 'connected' | 'disconnected' | 'reconnecting';

let hasConnectedOnce = false;
export function getHasConnectedOnce(): boolean { return hasConnectedOnce; }

function startWatchdog(socket: Socket, onStatus: (status: SocketStatus) => void) {
  // Heartbeat to localStorage — survives page crashes and lets us detect recovery
  if (!heartbeatTimer) {
    heartbeatTimer = setInterval(() => {
      try { localStorage.setItem(HEARTBEAT_KEY, String(Date.now())); } catch (e) { console.warn('[socketService] 心跳写入失败:', e); }
    }, HEARTBEAT_INTERVAL_MS);
  }

  const markConnected = () => { disconnectSince = null; hasConnectedOnce = true; onStatus('connected'); };
  const markDisconnected = () => { if (disconnectSince === null) disconnectSince = Date.now(); onStatus('disconnected'); };

  socket.on('connect', markConnected);
  socket.on('disconnect', markDisconnected);
  socket.io.on('reconnect_attempt', () => onStatus('reconnecting'));

  // 连接错误恢复：若 token 已变化，限频刷新 socket 鉴权，避免永久卡在重连
  let lastAuthRefresh = 0;
  socket.on('connect_error', () => {
    const now = Date.now();
    if (now - lastAuthRefresh > 30_000) {
      lastAuthRefresh = now;
      const fresh = getStoredToken();
      const current = (socket.auth as any)?.token;
      if (fresh && fresh !== current) {
        console.warn('[Watchdog] 检测到 token 变化，刷新 socket 鉴权');
        socket.auth = { token: fresh, fingerprint: DEVICE_FINGERPRINT };
        socket.connect();
      }
    }
  });

  // Periodic health check — NO page reload. socket.io auto-reconnects.
  const checkInterval = setInterval(() => {
    // Secondary health probe (informational only, never triggers reload)
    const token = getStoredToken();
    if (token && !socket.connected) {
      fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
        signal: AbortSignal.timeout(5000),
      }).catch(() => { /* 离线 — socket.io 会持续重试 */ });
    }
    // 极端兜底：仅当渲染器已断连 >10 分钟、socket.io 也无法恢复时才整页刷新
    if (disconnectSince && (Date.now() - disconnectSince) > DISCONNECT_FALLBACK_RELOAD_MS) {
      console.warn('[Watchdog] Socket disconnected >10min, last-resort reload to recover renderer');
      try { sessionStorage.setItem(SESSION_SKIP_BOOT, '1'); } catch {}
      clearInterval(checkInterval);
      window.location.reload();
    }
  }, HEALTHCHECK_INTERVAL_MS);

  // Visibility: when the user returns to the tab, ensure we are reconnecting
  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      if (!socket.connected && disconnectSince && (Date.now() - disconnectSince) > 30_000) {
        console.warn('[Watchdog] Page became visible but socket disconnected >30s, reconnecting');
        socket.connect();
      }
    }
  };
  document.addEventListener('visibilitychange', onVisible);

  // Return cleanup
  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    clearInterval(checkInterval);
  };
}


class SocketService {
  private socket: Socket | null = null;
  private token: string | null = null;
  private watchdogCleanup: (() => void) | null = null;

  private statusListeners = new Set<(status: SocketStatus) => void>();

  onStatus(cb: (status: SocketStatus) => void): () => void {
    this.statusListeners.add(cb);
    return () => { this.statusListeners.delete(cb); };
  }

  private emitStatus(status: SocketStatus) {
    this.statusListeners.forEach((cb) => {
      try { cb(status); } catch (e) { console.warn('[socketService] status 回调异常:', e); }
    });
  }

  connect() {
    const token = getStoredToken();

    if (!this.socket) {
      this.token = token;
      this.socket = io(getSocketOrigin(), {
        withCredentials: true,
        auth: { token, fingerprint: DEVICE_FINGERPRINT },
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
      });

      this.socket.on("connect", () => {
        console.log("[SocketService] Connected, id:", this.socket?.id);
      });

      this.socket.on("disconnect", (reason) => {
        console.log("[SocketService] Disconnected:", reason);
      });

      this.socket.on("connect_error", (err) => {
        console.error("[SocketService] Connect error:", err.message);
      });

      this.watchdogCleanup = startWatchdog(this.socket, (st) => this.emitStatus(st));
    } else if (token !== this.token) {
      this.token = token;
      this.socket.auth = { token, fingerprint: DEVICE_FINGERPRINT };
      this.socket.disconnect().connect();
    }
    return this.socket;
  }

  refreshAuth() {
    const token = getStoredToken();
    this.token = token;
    if (!this.socket) return null;
    this.socket.auth = { token, fingerprint: DEVICE_FINGERPRINT };
    if (this.socket.connected) {
      this.socket.disconnect().connect();
    } else {
      this.socket.connect();
    }
    return this.socket;
  }

  getSocket() {
    return this.socket;
  }

  disconnect() {
    if (this.watchdogCleanup) {
      this.watchdogCleanup();
      this.watchdogCleanup = null;
    }
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.token = null;
    }
  }
}

export const socketService = new SocketService();
